import { useCallback, useEffect, useRef, useState } from 'react';

import type { ApiClient, UserProfile } from '@solar/api-client';
import { ApiError } from '@solar/api-client';

export interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/**
 * 비동기 로딩 + 취소 처리.
 *
 * `alive` 플래그가 핵심이다. 현장 앱은 목록↔상세를 빠르게 오가는데, 느린
 * 3G 에서 이전 화면의 응답이 나중에 도착해 새 화면 상태를 덮어쓰는 문제가 있다.
 */
export function useAsync<T>(fetcher: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);

    fetcherRef
      .current()
      .then((value) => {
        if (alive) setData(value);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setError(err instanceof ApiError ? err.message : '불러오지 못했습니다.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
    // fetcher 는 ref 로 잡아 두었으므로 의존성은 호출자가 준 deps 와 재조회 nonce 뿐이다.
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, reload };
}

export interface Session {
  user: UserProfile | null;
  restoring: boolean;
  login: (userId: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

/**
 * 저장된 refresh 토큰으로 세션을 복구한다.
 *
 * 레거시 앱은 아이디/비밀번호를 SharedPreferences 평문으로 저장해 두고 매
 * 실행마다 재로그인했다. 여기서는 refresh 토큰만 Keychain/Keystore(네이티브)
 * 또는 sessionStorage(브라우저)에 둔다.
 */
export function useSession(api: ApiClient): Session {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [restoring, setRestoring] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .restoreSession()
      .then((profile) => {
        if (alive) setUser(profile);
      })
      .finally(() => {
        if (alive) setRestoring(false);
      });
    return () => {
      alive = false;
    };
  }, [api]);

  const login = useCallback(
    async (userId: string, password: string) => {
      const pair = await api.login(userId, password);
      setUser(pair.user);
    },
    [api],
  );

  const logout = useCallback(async () => {
    await api.logout();
    setUser(null);
  }, [api]);

  return { user, restoring, login, logout };
}
